import { FC, ReactNode, useState } from "react";
import Text from "apps/website/components/base/Text/Text";
import Table from "apps/website/components/feature/Table/Table";
import { themeRootClassMap } from "apps/website/maps/Theme.map";
import { ISummaryItem, ISummaryDiscountItem, ITitlePrice } from "@forms/schema";
import {
  legacySizeCollectionMap,
} from "apps/website/components/base/Text/Text.map";
import Image, { IImageProps } from "apps/website/components/base/Image/Image";

import { DisplayState } from "@/constants/state";

import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";
import IconButton from "../../base/IconButton/IconButton";

export interface ISummary {
  title: string;
  image?: IImageProps;
  originalPrice?: string;
  items: (ISummaryItem | ISummaryDiscountItem)[];
  footer?: ISummaryItem[];
  children?: ReactNode;
  state: DisplayState;
  canToggle?: boolean;
  isExpandedByDefault?: boolean;
  titlePrice?: ITitlePrice;
}

const cellStyle = "py-0 lg:py-2";
const leftCellStyle = `text-left px-4 ${cellStyle}`;
const rightCellStyle = `text-right pr-4 ${cellStyle}`;
const footerCellStyle = "pt-2 lg:pt-8";

const hiddenRowClassName = (isExpanded: boolean) => (isExpanded ? "h-auto" : "h-0 overflow-hidden hidden");

const SummaryHeaderPrice: FC<{ titlePrice?: ITitlePrice }> = ({ titlePrice }) => (
  <div className="flex justify-center items-center">
    { (titlePrice?.fullPrice && titlePrice?.discountedPrice) && (
      <Text tag="span" className="mr-2"><s>{ titlePrice.fullPrice }</s></Text>
    ) }
    <Text tag="span" display="title" size={legacySizeCollectionMap.bodyLg}>{ titlePrice?.discountedPrice }</Text>
  </div>
);

interface ISummaryHeaderProps {
  title: string;
  isExpanded: boolean;
  canToggle: boolean;
  titlePrice?: ITitlePrice;
  onToggle: () => void;
}

const SummaryHeader: FC<ISummaryHeaderProps> = ({ title, isExpanded, canToggle, titlePrice, onToggle }) => (
  <tr>
    <th
      className={`text-left px-4 ${themeRootClassMap.dark} ${canToggle ? "" : "py-2"}`}
      data-theme="dark"
      scope="col"
      colSpan={2}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Text tag="span" display="subtitle" size={legacySizeCollectionMap.bodyLg}>{ isExpanded ? title : "Plan summary" }</Text>
          { canToggle && (
            <IconButton icon={isExpanded ? "arrowUp" : "arrowDown" } label="Toggle summary" size="xsmall" onClick={onToggle} />
          ) }
        </div>
        { canToggle && !isExpanded && <SummaryHeaderPrice titlePrice={titlePrice} /> }
      </div>
    </th>
  </tr>
);

const SummaryItemPreBody: FC<{ preBody: ISummaryItem["preBody"] }> = ({ preBody }) => (
  <Text tag="span" className={`text-right pr-3 ${preBody?.strike ? "line-through" : ""}`} display={`${preBody?.bold ? "subtitle" : "default"}`} size={legacySizeCollectionMap.title2xs}>{ preBody?.value }</Text>
);

const summaryItemBodyDisplay = (body: ISummaryItem["body"]) => {
  if (!body?.bold) {
    return "default";
  }

  return body.uppercase ? "title" : "subtitle";
};

const SummaryItemBody: FC<{ body: ISummaryItem["body"] }> = ({ body }) => (
  <Text
    tag="span"
    className={`text-right ${body?.strike ? "line-through" : ""}`}
    size={legacySizeCollectionMap.bodySm}
    display={summaryItemBodyDisplay(body)}
    color={body?.highlight ? "success" : "default"}
  >{ body?.value }</Text>
);

interface ISummaryItemRowProps {
  item: ISummaryItem | ISummaryDiscountItem;
  isFirst: boolean;
  isExpanded: boolean;
}

const SummaryItemRow: FC<ISummaryItemRowProps> = ({ item, isFirst, isExpanded }) => (
  <tr aria-hidden={!isExpanded} className={hiddenRowClassName(isExpanded)}>
    <th scope="row" className={`${isFirst && "pt-2 lg:pt-4"} ${leftCellStyle}`}><Text tag="span" size={legacySizeCollectionMap.bodySm}>{ item?.title?.value }</Text></th>
    <td className={`${isFirst && "pt-2 lg:pt-4"} ${rightCellStyle}`}>
      <div className="flex flex-row justify-end items-center">
        <SummaryItemPreBody preBody={item?.preBody} />
        <SummaryItemBody body={item?.body} />
      </div>
      { item.bodyExtra?.value && (<Text tag="span" size={legacySizeCollectionMap.bodySm}>{ item.bodyExtra?.value } </Text>) }
    </td>
  </tr>
);

const summaryFooterTitleDisplay = (title: ISummaryItem["title"]) => (title?.bold ? "subtitle" : "default");
const summaryFooterTitleSize = (title: ISummaryItem["title"]) => (title?.bold ? legacySizeCollectionMap.bodyLg : legacySizeCollectionMap.bodySm);

const SummaryFooterTitle: FC<{ title: ISummaryItem["title"] }> = ({ title }) => (
  <Text tag="span" display={summaryFooterTitleDisplay(title)} size={summaryFooterTitleSize(title)}>{ title?.value }</Text>
);

const SummaryFooterBody: FC<{ foot: ISummaryItem }> = ({ foot }) => (
  foot.bodyExtra?.value ? (
    <Text tag="span" display="subtitle">{ foot.bodyExtra.value }</Text>
  ) : (
    <Text tag="span" display="subtitle" size={summaryFooterTitleSize(foot?.title)}>{ foot?.body?.value }</Text>
  )
);

interface ISummaryFooterRowProps {
  foot: ISummaryItem;
  isExpanded: boolean;
}

const SummaryFooterRow: FC<ISummaryFooterRowProps> = ({ foot, isExpanded }) => (
  <tr aria-hidden={!isExpanded} className={hiddenRowClassName(isExpanded)}>
    <th scope="row" className={`${footerCellStyle} ${leftCellStyle}`}><SummaryFooterTitle title={foot?.title} /></th>
    <td className={`${footerCellStyle} ${rightCellStyle}`}><SummaryFooterBody foot={foot} /></td>
  </tr>
);

interface ISummaryBodyProps {
  items: (ISummaryItem | ISummaryDiscountItem)[];
  footer?: ISummaryItem[];
  isExpanded: boolean;
  children?: ReactNode;
}

const SummaryBody: FC<ISummaryBodyProps> = ({ items, footer, isExpanded, children }) => (
  <>
    { items?.map(((item, index) => (
      <SummaryItemRow key={`${item?.title?.value}-${item?.body?.value}`} item={item} isFirst={index === 0} isExpanded={isExpanded} />
    ))) }
    <tr aria-hidden={!isExpanded} className={hiddenRowClassName(isExpanded)}>
      <td className="text-left px-4" colSpan={2}>
        { children }
      </td>
    </tr>
    { footer?.map(((foot) => (
      <SummaryFooterRow key={`${foot?.title?.value}-${foot?.body?.value}`} foot={foot} isExpanded={isExpanded} />
    ))) }
  </>
);

const Summary: FC<ISummary> = ({
  title,
  image,
  items,
  footer,
  state,
  canToggle = false,
  isExpandedByDefault = false,
  children,
  titlePrice,
}) => {

  const [ isExpanded, setIsExpanded ] = useState(isExpandedByDefault || !canToggle);

  return (
    <div data-component={Summary.name}>
      { image?.src && (
        <Image image={image} alt={title} />
      ) }
      <Table className="w-full border-collapse bg-white">
        <SummaryHeader
          title={title}
          isExpanded={isExpanded}
          canToggle={canToggle}
          titlePrice={titlePrice}
          onToggle={() => setIsExpanded(!isExpanded)}
        />
        { state === DisplayState.READY ? (
          <SummaryBody items={items} footer={footer} isExpanded={isExpanded}>
            { children }
          </SummaryBody>
        ) : (
          <tr>
            <td colSpan={2} className="flex justify-center items-center py-8">
              <LoadingSpinner color="black" size={32}/>
            </td>
          </tr>
        ) }
      </Table>
    </div>
  );
};

export default Summary;
